import { Col, Row, Space, Typography, useMediaQuery } from '@ag1/ui';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { TestimonialsV5 } from 'types/studio/studio';
import TestimonialsCard from '../TestimonialsCard/TestimonialsCard';
import TestimonialsHeading from '../TestimonialsHeading/TestimonialsHeading';
import clickableStyles from './clickableStyles.module.scss';
import styles from './styles.module.scss';

type Props = {
  addSlider: TestimonialsV5['addSlider'];
  cards: TestimonialsV5['cards'];
  subtitle: TestimonialsV5['subtitle'];
  headline: TestimonialsV5['headline'];
  textAlignment?: TestimonialsV5['textAlignment'];
  clickable?: TestimonialsV5['clickable'];
  makeSquare?: TestimonialsV5['makeSquare'];
  setOpened?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelected?: React.Dispatch<React.SetStateAction<Record<string, never>>>;
  wrapCards?: TestimonialsV5['wrapCards'];
  footnote?: TestimonialsV5['footnote'];
  visibleOnPageLoad: boolean;
} & Pick<TestimonialsV5, 'theme'>;

const TestimonialsContent = forwardRef<HTMLDivElement, Props>(
  (
    {
      addSlider,
      cards,
      subtitle,
      headline,
      textAlignment,
      theme,
      setOpened,
      setSelected,
      clickable,
      makeSquare,
      wrapCards,
      visibleOnPageLoad,
      footnote
    },
    ref
  ) => {
    const atSmall = useMediaQuery('s');

    const cardsNumberCorrespondsToSlider =
      clickable && !makeSquare ? cards && cards.length > 6 : cards && cards.length > 3;
    const colSpan = React.useMemo(() => {
      return cardsNumberCorrespondsToSlider ? { xxs: 12, s: 6, l: 4 } : { xxs: 12, l: 4 };
    }, [cardsNumberCorrespondsToSlider]);

    const colSpanSquare = React.useMemo(() => {
      return makeSquare ? { xxs: 12, s: 4, l: 3 } : { xxs: 12, l: 4 };
    }, [makeSquare]);
    return (
      <div className={styles.content} ref={ref}>
        <Row
          className={clsx(
            (clickable && clickableStyles.row) || (addSlider && styles.row),
            cardsNumberCorrespondsToSlider &&
              `${styles.rowSliderDesktopActive} ${wrapCards ? '' : styles.noWrap}`
          )}>
          {atSmall && cards?.length === 2 && (
            <Col span={{ l: 4 }}>
              <TestimonialsHeading
                sliderDesktopActive={false}
                subtitle={subtitle}
                headline={headline}
                theme={theme}
              />
            </Col>
          )}
          {cards &&
            cards.map((card, index) => {
              const { _key: key } = card;
              return (
                <Col
                  key={key}
                  span={makeSquare ? colSpanSquare : colSpan}
                  className={
                    (addSlider || cardsNumberCorrespondsToSlider ? styles.col : '') ||
                    (clickable ? clickableStyles.col : '')
                  }>
                  <Space top={{ s: index === 0 || cardsNumberCorrespondsToSlider ? 0 : 0, l: 0 }}>
                    <TestimonialsCard
                      {...card}
                      card={card}
                      cards={cards}
                      textAlignment={textAlignment}
                      theme={theme}
                      clickable={clickable}
                      setOpened={clickable ? setOpened : undefined}
                      setSelected={clickable ? setSelected : undefined}
                      makeSquare={makeSquare}
                      visibleOnPageLoad={visibleOnPageLoad}
                    />
                  </Space>
                </Col>
              );
            })}
        </Row>
        {footnote && (
          <Typography
            variant="portalCaption"
            size="2"
            color="grey-1-new"
            noPadding
            className={clsx(styles.footnote, cards?.length === 2 && styles.footnoteCards2)}>
            <CustomPortableText value={footnote} />
          </Typography>
        )}
      </div>
    );
  }
);

if (process.env.NODE_ENV !== 'production') {
  TestimonialsContent.displayName = 'TestimonialsContent';
}

export default TestimonialsContent;
